import React from 'react';
import { X, Package, MapPin, Calendar, CreditCard, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';

const OrderDetailModal = ({ order, isOpen, onClose }) => {
  if (!isOpen || !order) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-red-100 text-red-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    };
    return texts[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-black text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h3 className="text-2xl font-bold">Order Details</h3>
            <p className="text-sm opacity-90">#{order.order_number}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Order Status & Info */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Order Date
                </p>
                <p className="font-semibold dark:text-white">{formatDate(order.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Status</p>
                <Badge className={getStatusColor(order.status || order.order_status)}>
                  {getStatusText(order.status || order.order_status)}
                </Badge>
              </div>
              {order.payment_method && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payment Method
                  </p>
                  <p className="font-semibold dark:text-white capitalize">{order.payment_method}</p>
                </div>
              )}
              {order.tracking_number && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tracking Number</p>
                  <p className="font-semibold text-red-600">{order.tracking_number}</p>
                </div>
              )}
            </div>
            
            {order.shipped_at && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Shipped Date: {formatDate(order.shipped_at)}
                </p>
              </div>
            )}
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div>
              <h4 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800 dark:text-white">
                <MapPin className="w-5 h-5 text-red-600" />
                Shipping Address
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="font-semibold dark:text-white">
                  {order.shipping_address.first_name} {order.shipping_address.last_name}
                </p>
                <p className="text-gray-700 dark:text-gray-300 mt-2">{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && (
                  <p className="text-gray-700 dark:text-gray-300">{order.shipping_address.address_line2}</p>
                )}
                <p className="text-gray-700 dark:text-gray-300">
                  {order.shipping_address.city}, {order.shipping_address.state_province} {order.shipping_address.postal_code}
                </p>
                <p className="text-gray-700 dark:text-gray-300">{order.shipping_address.country}</p>
                {order.shipping_address.phone && (
                  <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
                    Phone: {order.shipping_address.phone}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Order Items */}
          <div>
            <h4 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800 dark:text-white">
              <Package className="w-5 h-5 text-red-600" />
              Order Items
            </h4>
            <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Product</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Quantity</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Price</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-600">
                    {order.items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 dark:text-gray-300">
                          {item.name || item.product_title || item.product_id}
                          {item.variant_title && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">
                              {item.variant_title}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center dark:text-gray-300">{item.quantity}</td>
                        <td className="px-4 py-3 text-right dark:text-gray-300">
                          ${parseFloat(item.price || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold dark:text-gray-300">
                          ${(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Order Summary */}
              <div className="bg-gradient-to-r from-red-50 to-gray-50 dark:from-red-900/20 dark:to-gray-800 px-4 py-4 space-y-2">
                {/* Subtotal */}
                <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                  <span>Subtotal:</span>
                  <span>${parseFloat(order.subtotal || order.total || 0).toFixed(2)}</span>
                </div>
                
                {/* Coupon Discount */}
                {order.coupon_code && order.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-green-600 dark:text-green-400">
                    <span className="flex items-center gap-1">
                      <span>Coupon ({order.coupon_code}):</span>
                    </span>
                    <span>-${parseFloat(order.discount_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                
                {/* Tax */}
                {order.tax_amount && order.tax_amount > 0 && (
                  <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                    <span>Tax:</span>
                    <span>${parseFloat(order.tax_amount || 0).toFixed(2)}</span>
                  </div>
                )}
                
                {/* Shipping */}
                {order.shipping_cost && order.shipping_cost > 0 && (
                  <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                    <span>Shipping:</span>
                    <span>${parseFloat(order.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                )}
                
                {/* Divider */}
                <div className="border-t border-gray-300 dark:border-gray-600 my-2"></div>
                
                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-800 dark:text-white">Total Amount:</span>
                  <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                    ${parseFloat(order.total || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Notes if any */}
          {order.notes && (
            <div>
              <h4 className="font-bold text-lg mb-2 text-gray-800 dark:text-white">Notes</h4>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">{order.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end rounded-b-lg border-t dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-red-600 to-black text-white rounded-lg hover:from-red-700 hover:to-gray-900 transition-all shadow-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
